<template>
  <div class="order-query">
    <div class="query-nav">
      <button @click="goBack" class="btn-plain">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        <span>返回首页</span>
      </button>
    </div>

    <div class="ios-group-header">卡密服务</div>

    <div class="mode-tabs" role="tablist">
      <button
        type="button"
        :class="{ active: mode === 'query' }"
        @click="switchMode('query')"
      >
        查询订单
      </button>
      <button
        type="button"
        :class="{ active: mode === 'exchange' }"
        @click="switchMode('exchange')"
      >
        调换卡密
      </button>
    </div>

    <div class="ios-card card-main">
      <div class="card-icon-row">
        <div class="card-icon" :class="{ exchange: mode === 'exchange' }">
          <svg v-if="mode === 'query'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 1l4 4-4 4"></path>
            <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
            <path d="M7 23l-4-4 4-4"></path>
            <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
          </svg>
        </div>
        <div>
          <div class="card-title">{{ modeTitle }}</div>
          <div class="card-desc">{{ modeDesc }}</div>
        </div>
      </div>

      <div class="input-group">
        <label class="ios-field-label" for="cardCodes">卡密编号</label>
        <textarea
          id="cardCodes"
          v-model="cardCodesText"
          class="ios-textarea"
          :class="{ 'has-error': error }"
          rows="5"
          placeholder="每行一个卡密，最多 50 个"
          @keydown.ctrl.enter.prevent="submit"
          @keydown.meta.enter.prevent="submit"
        ></textarea>
        <div class="input-meta">
          <span>已识别 {{ parsedCardCodes.length }} 个</span>
          <button v-if="cardCodesText" type="button" @click="reset" class="link-button">清空</button>
        </div>
        <div v-if="error" class="ios-error-text">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ error }}</span>
        </div>
      </div>

      <div v-if="mode === 'exchange'" class="input-group">
        <label class="ios-field-label" for="exchangePassword">调换密码</label>
        <input
          id="exchangePassword"
          v-model.trim="exchangePassword"
          class="ios-input"
          :class="{ 'has-error': passwordError }"
          type="password"
          placeholder="请输入调换密码"
          autocomplete="current-password"
          @keydown.ctrl.enter.prevent="submit"
          @keydown.meta.enter.prevent="submit"
        />
        <div v-if="passwordError" class="ios-error-text">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ passwordError }}</span>
        </div>
      </div>

      <button
        @click="submit"
        class="btn-filled"
        :class="{ 'btn-destructive': mode === 'exchange' }"
        :disabled="!parsedCardCodes.length || (mode === 'exchange' && !exchangePassword.trim()) || loading"
      >
        <span v-if="loading" class="ios-spinner"></span>
        {{ loading ? loadingText : actionText }}
      </button>
    </div>

    <template v-if="hasQueryResult">
      <div class="ios-group-header">查询结果</div>
      <div class="ios-card query-summary">
        <div class="summary-stats">
          <div class="summary-stat">
            <span>查询总数</span>
            <strong>{{ queryTotal }}</strong>
          </div>
          <div class="summary-stat">
            <span>可用卡密数</span>
            <strong class="status-available">{{ availableCount }}</strong>
          </div>
        </div>
        <button
          type="button"
          class="btn-tinted copy-available-button"
          :disabled="!availableCardCodes.length"
          @click="copyAvailableCardCodes"
        >
          {{ queryCopied ? '已复制' : '复制可用卡密' }}
        </button>
      </div>
      <div class="result-list">
        <div
          v-for="(item, index) in displayQueryResults"
          :key="`${item.cardCode}-${index}`"
          class="ios-card result-card"
          :class="{ missing: isMissingResult(item) }"
        >
          <div class="result-head">
            <span class="result-code">{{ item.cardCode }}</span>
            <span v-if="item.cardValid" class="ios-row-value status-available">
              {{ item.cardStatusText || '可用' }}
            </span>
            <span v-else-if="item.found" class="ios-row-value" :class="getStatusClass(item.status)">
              {{ getStatusText(item.status) }}
            </span>
            <span v-else class="ios-row-value" :class="getCardStatusClass(item)">
              {{ item.cardStatusText || '未找到' }}
            </span>
          </div>

          <div v-if="item.found" class="result-grid">
            <div>
              <span>订单编号</span>
              <strong>{{ item.orderId }}</strong>
            </div>
            <div>
              <span>商品名称</span>
              <strong>{{ item.productName }}</strong>
            </div>
            <div>
              <span>目标邮箱</span>
              <strong>{{ item.email }}</strong>
            </div>
            <div>
              <span>创建时间</span>
              <strong>{{ formatTime(item.createTime) }}</strong>
            </div>
          </div>

          <div v-else-if="item.cardExists" class="result-grid">
            <div>
              <span>卡密状态</span>
              <strong :class="getCardStatusClass(item)">{{ item.cardStatusText }}</strong>
            </div>
            <div>
              <span>商品名称</span>
              <strong>{{ item.productName || '-' }}</strong>
            </div>
            <div>
              <span>商品别名</span>
              <strong>{{ item.productAlias || '-' }}</strong>
            </div>
            <div>
              <span>订单状态</span>
              <strong>{{ item.message }}</strong>
            </div>
          </div>

          <div v-else class="missing-message">{{ item.message }}</div>
        </div>
      </div>
      <div class="ios-group-footer">
        查询结果会汇总本次提交数量和接口判定的可用卡密；可用卡密支持一键复制。
      </div>
    </template>

    <template v-if="exchangeResult">
      <div class="ios-group-header">调换结果</div>
      <div class="ios-card exchange-result">
        <div class="ios-callout is-warning exchange-notice">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>温馨提示：本结果只展示一次，请妥善保管，兑换成功后请及时复制或下载新卡密。</span>
        </div>
        <div class="exchange-summary">
          <strong>已调换 {{ exchangeResult.exchanged }} 个卡密</strong>
          <div class="exchange-actions">
            <button type="button" class="btn-tinted copy-button" @click="copyExchangeContent">
              {{ copied ? '已复制' : '复制新卡密' }}
            </button>
            <button type="button" class="btn-tinted download-button" @click="downloadExchangeContent">
              下载新卡密
            </button>
          </div>
        </div>
        <textarea
          class="ios-textarea exchange-content"
          :value="exchangeResult.content || exchangeResult.cardCodes.join('\n')"
          readonly
          rows="5"
        ></textarea>
      </div>

      <div v-if="exchangeResult.records && exchangeResult.records.length" class="result-list exchange-records">
        <div
          v-for="(record, index) in exchangeResult.records"
          :key="`${record.oldCardCode}-${index}`"
          class="ios-card result-card"
        >
          <div class="result-grid">
            <div>
              <span>原卡密</span>
              <strong>{{ record.oldCardCode }}</strong>
            </div>
            <div>
              <span>新卡密</span>
              <strong>{{ record.newCardCode }}</strong>
            </div>
            <div>
              <span>商品名称</span>
              <strong>{{ record.productName }}</strong>
            </div>
            <div>
              <span>商品别名</span>
              <strong>{{ record.productAlias }}</strong>
            </div>
          </div>
        </div>
      </div>

      <div class="ios-group-footer">
        调换成功后原卡密会被禁用，请妥善保存新卡密。
      </div>
    </template>
  </div>
</template>

<script>
import { exchangeCards, queryOrdersByCards } from '../services/api'
import {
  getOrderStatusClass,
  getOrderStatusText
} from '../constants/orderStatus'

const MAX_CARD_CODES = 50

export default {
  name: 'OrderQuery',
  data() {
    return {
      mode: 'query',
      cardCodesText: '',
      exchangePassword: '',
      queryResults: [],
      submittedCardCodes: [],
      exchangeResult: null,
      loading: false,
      error: '',
      passwordError: '',
      copied: false,
      queryCopied: false
    }
  },
  computed: {
    parsedCardCodes() {
      return this.cardCodesText
        .split(/[\s,，;；]+/)
        .map((code) => code.trim())
        .filter(Boolean)
    },
    modeTitle() {
      return this.mode === 'query' ? '查询充值订单' : '批量调换卡密'
    },
    modeDesc() {
      return this.mode === 'query'
        ? '输入一个或多个卡密，按顺序查询订单状态'
        : '输入未使用卡密，生成同商品的新卡密'
    },
    actionText() {
      return this.mode === 'query' ? '查询订单' : '确认调换'
    },
    loadingText() {
      return this.mode === 'query' ? '查询中...' : '调换中...'
    },
    queryTotal() {
      return this.queryResults.length || this.submittedCardCodes.length
    },
    availableCardCodes() {
      return this.queryResults
        .filter((item) => item.cardValid === true)
        .map((item) => item.cardCode)
        .filter(Boolean)
    },
    availableCount() {
      return this.availableCardCodes.length
    },
    displayQueryResults() {
      return this.queryResults
    },
    hasQueryResult() {
      return this.queryResults.length > 0
    }
  },
  methods: {
    switchMode(mode) {
      this.mode = mode
      this.error = ''
      this.passwordError = ''
      this.queryResults = []
      this.submittedCardCodes = []
      this.exchangeResult = null
      this.copied = false
      this.queryCopied = false
    },
    validateInput() {
      this.error = ''
      this.passwordError = ''
      const cardCodes = this.parsedCardCodes

      if (!cardCodes.length) {
        this.error = '请输入卡密编号'
        return null
      }

      if (cardCodes.length > MAX_CARD_CODES) {
        this.error = `单次最多处理 ${MAX_CARD_CODES} 个卡密`
        return null
      }

      const duplicated = cardCodes.find((code, index) => cardCodes.indexOf(code) !== index)
      if (duplicated) {
        this.error = `存在重复卡密：${duplicated}`
        return null
      }

      if (this.mode === 'exchange' && !this.exchangePassword.trim()) {
        this.passwordError = '请输入调换密码'
        return null
      }

      return cardCodes
    },
    async submit() {
      const cardCodes = this.validateInput()
      if (!cardCodes) return

      this.loading = true
      this.error = ''
      this.passwordError = ''
      this.queryResults = []
      this.submittedCardCodes = this.mode === 'query' ? cardCodes : []
      this.exchangeResult = null
      this.copied = false
      this.queryCopied = false

      const response = this.mode === 'query'
        ? await queryOrdersByCards(cardCodes)
        : await exchangeCards(cardCodes, this.exchangePassword.trim())

      if (response.code === 200) {
        if (this.mode === 'query') {
          this.queryResults = Array.isArray(response.data) ? response.data : []
        } else {
          this.exchangeResult = {
            ...response.data,
            cardCodes: response.data.cardCodes || [],
            records: response.data.records || []
          }
        }
      } else {
        this.error = response.message || (this.mode === 'query' ? '查询失败' : '调换失败')
      }

      this.loading = false
    },
    isMissingResult(item) {
      return item && item.cardExists === false
    },
    getCardStatusClass(item) {
      if (!item || item.cardExists === false) return 'status-unknown'
      if (item.cardValid === true || item.cardStatus === 1 || item.cardStatus === '1') return 'status-available'
      if (item.cardStatus === 2 || item.cardStatus === '2') return 'status-used'
      return 'status-unavailable'
    },
    async copyAvailableCardCodes() {
      const content = this.availableCardCodes.join('\n')
      if (!content) return

      try {
        await navigator.clipboard.writeText(content)
        this.queryCopied = true
        setTimeout(() => {
          this.queryCopied = false
        }, 1600)
      } catch {
        this.error = '复制失败，请手动复制可用卡密'
      }
    },
    async copyExchangeContent() {
      if (!this.exchangeResult) return

      const content = this.exchangeResult.content || this.exchangeResult.cardCodes.join('\n')
      try {
        await navigator.clipboard.writeText(content)
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 1600)
      } catch {
        this.error = '复制失败，请手动复制新卡密'
      }
    },
    downloadExchangeContent() {
      if (!this.exchangeResult) return

      const content = this.exchangeResult.content || this.exchangeResult.cardCodes.join('\n')
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

      link.href = url
      link.download = `new-card-codes-${timestamp}.txt`
      link.click()
      URL.revokeObjectURL(url)
    },
    getStatusClass(status) {
      return getOrderStatusClass(status)
    },
    getStatusText(status) {
      return getOrderStatusText(status)
    },
    formatTime(timeStr) {
      if (!timeStr) return ''
      try {
        const date = new Date(timeStr)
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch {
        return timeStr
      }
    },
    reset() {
      this.cardCodesText = ''
      this.exchangePassword = ''
      this.queryResults = []
      this.submittedCardCodes = []
      this.exchangeResult = null
      this.error = ''
      this.passwordError = ''
      this.copied = false
      this.queryCopied = false
    },
    goBack() {
      this.$emit('back')
    }
  }
}
</script>

<style scoped>
.query-nav {
  display: flex;
  margin: 4px 0 0 -8px;
}

.mode-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: var(--ios-fill);
}

.mode-tabs button {
  min-height: 38px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--ios-label-secondary);
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.mode-tabs button.active {
  background: var(--ios-card);
  color: var(--ios-label);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card-main {
  margin-top: 14px;
  padding: 26px 22px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-icon-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 11px;
  background: rgba(0, 122, 255, 0.1);
  color: var(--ios-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon.exchange {
  background: rgba(255, 149, 0, 0.12);
  color: var(--ios-orange);
}

.card-icon svg {
  width: 22px;
  height: 22px;
}

.card-title {
  font-size: 19px;
  font-weight: 600;
}

.card-desc {
  margin-top: 2px;
  font-size: 14px;
  color: var(--ios-label-secondary);
}

.input-group {
  display: flex;
  flex-direction: column;
}

.input-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 13px;
  color: var(--ios-label-secondary);
}

.link-button {
  border: none;
  background: transparent;
  color: var(--ios-blue);
  font: inherit;
  cursor: pointer;
}

.result-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.query-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 12px;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  flex: 1;
}

.summary-stat {
  min-width: 0;
}

.summary-stat span {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--ios-label-secondary);
}

.summary-stat strong {
  display: block;
  font-size: 22px;
  line-height: 1.1;
}

.copy-available-button {
  border-radius: 12px;
  flex-shrink: 0;
}

.copy-available-button:disabled {
  color: var(--ios-label-tertiary);
  background: var(--ios-fill);
  cursor: not-allowed;
}

.result-card {
  padding: 16px 18px;
  animation: fadeIn 0.35s ease;
}

.result-card.missing {
  background: rgba(255, 255, 255, 0.72);
}

.result-head,
.exchange-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.result-code {
  font-size: 16px;
  font-weight: 700;
  word-break: break-all;
}

.result-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
  margin-top: 14px;
}

.result-grid div {
  min-width: 0;
}

.result-grid span {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--ios-label-secondary);
}

.result-grid strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}

.missing-message {
  margin-top: 10px;
  color: var(--ios-label-secondary);
  font-size: 14px;
}

.exchange-result {
  padding: 18px;
}

.exchange-notice {
  margin-bottom: 16px;
}

.exchange-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.copy-button,
.download-button {
  border-radius: 12px;
}

.exchange-content {
  margin-top: 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px;
}

.exchange-records {
  margin-top: 12px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .card-main {
    padding: 16px 14px;
  }

  .result-grid {
    grid-template-columns: 1fr;
  }

  .query-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .copy-available-button {
    width: 100%;
  }

  .exchange-summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .exchange-actions {
    width: 100%;
  }

  .exchange-actions .btn-tinted {
    flex: 1;
    padding-left: 12px;
    padding-right: 12px;
  }
}
</style>
